<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcInvoiceExchangeRateEditor',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    currency: {
      type: String,
      required: true,
    },
    value: {
      type: Object,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const labelAlign = ref('right');
  const item = ref(null);
  const formId = ref(null);
  const currencies = ref(null);
  const triggerButtonID = ref(null);
  const currentTitle = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    labelAlign: labelAlign.value,
    item: item.value,
    formId: formId.value,
    currencies: currencies.value,
    triggerButtonID: triggerButtonID.value,
    currentTitle: currentTitle.value,
    ux: ux.value,
  }));

  const $$load_item = async () => {
    return _.extend(
      {
        currency_to: null,
        value: 1,
        date: $fn.value.today(),
        bank: null,
      },
      props.value,
      { currency_from: props.currency }
    );
  };
  const $$load_formId = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_triggerButtonID = async () => {
    return $fn.value.randomElementId();
  };
  const $$load_currentTitle = async () => {
    return $fn.value.gettext('Exchange rate') + ' - ' + props.currency;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'labelAlign' },
      { name: 'item' },
      { name: 'formId' },
      { name: 'currencies' },
      { name: 'triggerButtonID' },
      { name: 'currentTitle' },
      { name: 'ux' },
    ],
    loaders: {
      item: $$load_item,
      formId: $$load_formId,
      currencies: $$load_currencies,
      triggerButtonID: $$load_triggerButtonID,
      currentTitle: $$load_currentTitle,
    },
    ux: {},
    dataRefs: {
      labelAlign,
      item,
      formId,
      currencies,
      triggerButtonID,
      currentTitle,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.currency,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('currency');
    }
  );
  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );

  const act_7315092382398562_reloadComponentData_wqgfw0sjq =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_qy1y4v41k =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_wqgfw0sjq,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_qy1y4v41k],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_qy1y4v41k,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_qy1y4v41k = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_qy1y4v41k.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpButton icon="fa-list-alt" variant="default" :id="triggerButtonID">
    </ItpButton>
    <BPopover
      :target="triggerButtonID"
      triggers="click blur"
      :title="currentTitle"
    >
      <ItpFormGroup
        name="currency_to"
        :label-cols-md="4"
        :content-cols-md="8"
        :label="gettext('Currency')"
        :labelAlign="labelAlign"
      >
        <ItpFormSelect2
          name="currency_to"
          valueField="code"
          textField="code"
          required
          v-model="item.currency_to"
          :value="item.currency_to"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.currency_to =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
          :options="currencies"
        >
        </ItpFormSelect2>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="4"
        :content-cols-md="8"
        name="value"
        :label="gettext('Amount')"
      >
        <ItpFormInput
          mask="number"
          unmask="typed"
          :scale="2"
          name="value"
          v-model="item.value"
          :value="item.value"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.value =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormInput>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="4"
        :content-cols-md="8"
        name="date"
        :label="gettext('Date')"
      >
        <ItpFormDatetime name="date" v-model="item.date"> </ItpFormDatetime>
      </ItpFormGroup>
      <ItpFormGroup
        :label-cols-md="4"
        :content-cols-md="8"
        name="bank"
        :label="gettext('Bank')"
      >
        <ItpFormInput
          name="bank"
          v-model="item.bank"
          :value="item.bank"
          @input="
            !($event && typeof $event.stopPropagation === 'function') &&
            (item.bank =
              $event && typeof $event === 'object' && 'data' in $event
                ? $event.data
                : $event)
          "
        >
        </ItpFormInput>
      </ItpFormGroup>
    </BPopover>
  </ItpUsercontrol>
</template>
